import type { Order } from '../api/types'

export const FLATTEN_TIMEOUT_MS = 5000
const POLL_INTERVAL_MS = 700

const TERMINAL_STATUSES = new Set([
  'FILLED', 'CANCELLED', 'CANCELED', 'REJECTED', 'EXPIRED', 'REPLACED',
])

/**
 * QUEUED_CANCELLED (Public, cancel queued for next session) deliberately
 * counts as OPEN — the order can still fill at the next open, so Flatten
 * must not sell on top of it.
 */
export function isOpenOrder(o: Order): boolean {
  return !TERMINAL_STATUSES.has(String(o.status).toUpperCase()) && !o.closedAt
}

export interface FlattenDeps {
  /** Open orders for the target symbol (exact symbol match). */
  getOpenOrders: () => Promise<Order[]>
  cancelOrder: (orderId: string) => Promise<unknown>
  /** Fresh position quantity (signed), read AFTER cancels confirm. */
  getPositionQty: () => Promise<number>
  placeSellAll: (qty: number) => Promise<unknown>
  sleep?: (ms: number) => Promise<void>
  timeoutMs?: number
  onProgress?: (msg: string) => void
}

export type FlattenResult =
  | { ok: true; cancelled: number; soldQty: number }
  | { ok: false; reason: string; cancelled: number }

/**
 * Flatten (see CONTEXT.md): cancel all open symbol orders -> confirm cancels
 * (abort on timeout rather than risk an over-sell) -> re-read quantity ->
 * sell everything.
 */
export async function flattenSymbol(deps: FlattenDeps): Promise<FlattenResult> {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const timeoutMs = deps.timeoutMs ?? FLATTEN_TIMEOUT_MS

  const open = (await deps.getOpenOrders()).filter(isOpenOrder)
  if (open.length > 0) {
    deps.onProgress?.(`Cancelling ${open.length} order${open.length === 1 ? '' : 's'}…`)
    await Promise.allSettled(open.map((o) => deps.cancelOrder(o.orderId)))

    const deadline = Date.now() + timeoutMs
    let remaining = open.length
    for (;;) {
      remaining = (await deps.getOpenOrders()).filter(isOpenOrder).length
      if (remaining === 0) break
      if (Date.now() >= deadline) {
        return {
          ok: false,
          cancelled: open.length - remaining,
          reason: `${remaining} order${remaining === 1 ? '' : 's'} still open after cancel — not selling`,
        }
      }
      await sleep(POLL_INTERVAL_MS)
    }
  }

  const qty = await deps.getPositionQty()
  if (!(qty > 0)) return { ok: true, cancelled: open.length, soldQty: 0 }

  deps.onProgress?.(`Selling ${qty}…`)
  await deps.placeSellAll(qty)
  return { ok: true, cancelled: open.length, soldQty: qty }
}
